import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatBadge, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, Handshake, Copy, ChevronRight, Building } from "lucide-react";
import ViewToggle, { useViewMode } from "@/components/admin/ViewToggle";
import { useLang } from "@/lib/lang";

const TYPES = [
  { k: "hotel", l: "Hotel" },
  { k: "travel_agency", l: "Agência de Viagens" },
  { k: "fleet", l: "Frota" },
];

export default function Partners() {
  const { t } = useLang();
  const nav = useNavigate();
  const [partners, setPartners] = useState([]);
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [mode, setMode] = useViewMode("partners");
  const load = () => api.get("/admin/partners").then((r) => setPartners(r.data));
  useEffect(() => { load(); }, []);
  const updateStatus = async (p, status, e) => {
    if (e) e.stopPropagation();
    try { await api.patch(`/admin/partners/${p.id}`, { status }); toast.success(`${t("nav_partners")} · ${status}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const copy = (text, e) => {
    if (e) e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(() => toast.success(t("toast_copied"))).catch(() => toast.error(t("toast_clipboard_unavailable")));
      } else {
        toast.error(t("toast_clipboard_unavailable"));
      }
    } catch { toast.error(t("toast_clipboard_unavailable")); }
  };
  const open = (p) => nav(`/admin/partners/${p.id}`);

  return (
    <div className="p-10 max-w-[1600px]">
      <PageHeader eyebrow="Distribution" title={t("nav_partners")} description="Hotéis, agências, tour operators, contas corporativas e tripulação — com preços, comissões e links dedicados.">
        <ViewToggle scope="partners" value={mode} onChange={setMode} testId="partners-view-toggle" />
        <GoldButton onClick={() => setShow(true)} data-testid="new-partner-btn"><Plus className="w-4 h-4" /> {t("add")}</GoldButton>
      </PageHeader>

      {partners.length === 0 && <div className="border border-dashed border-slate-200 p-16 text-center text-slate-500">Sem parceiros.</div>}
      {mode === "cards" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {partners.map((p) => (
            <div key={p.id} data-testid={`partner-${p.id}`} onClick={() => open(p)} className="border border-slate-200 bg-white p-6 cursor-pointer hover:border-[#f59e0b] transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 border border-slate-200 flex items-center justify-center">
                  <Handshake className="w-5 h-5 text-[#f59e0b]" />
                </div>
                <StatBadge status={p.status} />
              </div>
              <p className="label-caps mb-1">{TYPES.find(t => t.k === p.type)?.l || p.type}</p>
              <h3 className="font-display text-lg text-[#0a0f1e]">{p.company_name}</h3>
              <div className="text-xs text-slate-600 mt-1 font-semibold">NIF {p.nif}</div>
              <div className="mt-4 space-y-1.5 text-xs">
                <Row label="Contacto" value={p.contact_person || "—"} />
                <Row label="Comissão" value={<span className="text-[#f59e0b] font-mono font-bold">{p.commission_pct}% · {p.commission_direction === "partner_earns" ? "ganha" : "desconto"}</span>} />
                <Row label="Faturação" value={p.billing_cycle === "monthly" ? "Mensal" : "Por reserva"} />
                <Row label="Crédito" value={<span className="font-mono font-semibold">€{(p.credit_limit_eur || 0).toFixed(0)}</span>} />
                <Row label="Slug" value={<span className="font-mono text-[#f59e0b] font-bold">{p.slug || "—"}</span>} />
              </div>
              <button onClick={(e) => { e.stopPropagation(); setExpanded(expanded === p.id ? null : p.id); }} className="mt-4 w-full text-[10px] uppercase border border-slate-200 py-2 hover:border-[#f59e0b] text-slate-600 hover:text-[#f59e0b] flex items-center justify-center gap-1 font-semibold" data-testid={`expand-${p.id}`}>
                {expanded === p.id ? t("action_hide") : t("action_show")} widget & API <ChevronRight className={`w-3 h-3 transition-transform ${expanded === p.id ? "rotate-90" : ""}`} />
              </button>
              {expanded === p.id && (
                <div className="mt-3 space-y-3 border-t border-slate-200 pt-3" onClick={(e) => e.stopPropagation()}>
                  <div>
                    <div className="label-caps mb-1">{t("partners_booking_link")}</div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-[10px] font-mono text-[#0a0f1e] bg-white p-2 border border-slate-200 truncate">{window.location.origin}/book/{p.slug}</code>
                      <button onClick={(e) => copy(`${window.location.origin}/book/${p.slug}`, e)} className="text-slate-500 hover:text-[#f59e0b]"><Copy className="w-3 h-3" /></button>
                    </div>
                  </div>
                  <div>
                    <div className="label-caps mb-1">Widget embed</div>
                    <pre className="text-[10px] font-mono text-slate-700 bg-white p-2 border border-slate-200 overflow-x-auto">{`<iframe src="${window.location.origin}/book/${p.slug}" width="100%" height="620" frameborder="0"></iframe>`}</pre>
                  </div>
                  <div>
                    <div className="label-caps mb-1">{t("partners_api_key")}</div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-[10px] font-mono text-emerald-700 bg-white p-2 border border-slate-200 truncate">{p.api_key || "—"}</code>
                      <button onClick={(e) => copy(p.api_key, e)} className="text-slate-500 hover:text-[#f59e0b]"><Copy className="w-3 h-3" /></button>
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-4 flex gap-2 items-center">
                {p.status !== "approved" && <GoldButton onClick={(e) => updateStatus(p, "approved", e)} className="!h-8 !px-3 !text-[10px]" data-testid={`approve-partner-${p.id}`}>{t("approve")}</GoldButton>}
                {p.status !== "suspended" && <GhostButton onClick={(e) => updateStatus(p, "suspended", e)} className="!h-8 !px-3 !text-[10px]">{t("status_suspended")}</GhostButton>}
                <Link to={`/admin/partners/${p.id}`} onClick={(e) => e.stopPropagation()} data-testid={`open-partner-${p.id}`} className="ml-auto text-[10px] uppercase tracking-widest text-[#f59e0b] hover:underline font-bold inline-flex items-center gap-1">
                  {t("action_open")}
                </Link>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="partners-list-view">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700">Empresa</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700">Tipo</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700">NIF</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700">Contacto</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700 text-right">Comissão</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700">Slug</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700">{t("status")}</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700 text-right">{t("actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {partners.map((p) => (
                <tr key={p.id} onClick={() => open(p)} data-testid={`partner-row-${p.id}`} className="hover:bg-slate-50 cursor-pointer">
                  <td className="px-4 py-3 font-semibold text-[#0a0f1e]">{p.company_name}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{TYPES.find(x => x.k === p.type)?.l || p.type}</td>
                  <td className="px-4 py-3 text-slate-700 font-mono text-xs">{p.nif}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{p.contact_person || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#f59e0b] font-bold">{p.commission_pct}%</td>
                  <td className="px-4 py-3 font-mono text-[#f59e0b] font-bold text-xs">{p.slug}</td>
                  <td className="px-4 py-3"><StatBadge status={p.status} /></td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {p.status !== "approved" && <button onClick={(e) => updateStatus(p, "approved", e)} className="text-[10px] uppercase tracking-widest text-emerald-700 hover:text-emerald-900 font-bold">{t("approve")}</button>}
                    {p.status !== "suspended" && <button onClick={(e) => updateStatus(p, "suspended", e)} className="text-[10px] uppercase tracking-widest text-slate-700 hover:text-red-700 font-bold">{t("status_suspended")}</button>}
                    <Link to={`/admin/partners/${p.id}`} onClick={(e) => e.stopPropagation()} data-testid={`open-partner-row-${p.id}`} className="text-[10px] uppercase tracking-widest text-[#f59e0b] hover:underline font-bold">Abrir →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {show && <CreatePartner onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }} />}
    </div>
  );
}

function Row({ label, value }) {
  return (<div className="flex justify-between text-slate-500"><span>{label}</span><span className="text-[#0a0f1e]">{value}</span></div>);
}

function CreatePartner({ onClose, onCreated }) {
  const { t } = useLang();
  const [f, setF] = useState({
    company_name: "", nif: "", type: "hotel", contact_person: "", email: "", phone: "",
    commission_pct: 10, commission_direction: "partner_earns",
    credit_limit_eur: 0, billing_cycle: "per_booking", slug: "",
    postal_code: "", allow_client_payment: false, invoice_allow_generic: true,
  });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/admin/partners", f); toast.success(t("partners_added_toast")); onCreated(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-6">
      <form onSubmit={submit} className="bg-white border border-[#f59e0b]/30 w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl text-[#0a0f1e]">{t("partners_new")}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Fld l="Company name"><Input required value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} className="input-dt" data-testid="p-name" /></Fld>
          <Fld l="NIF"><Input required value={f.nif} onChange={(e) => setF({ ...f, nif: e.target.value })} className="input-dt" /></Fld>
          <Fld l="Type">
            <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="input-dt" data-testid="p-type">
              {TYPES.map((t) => <option key={t.k} value={t.k}>{t.l}</option>)}
            </select>
          </Fld>
          <Fld l="Slug (URL)"><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} placeholder="hotel-bairro-alto" className="input-dt" data-testid="p-slug" /></Fld>
          <Fld l="Contact person"><Input value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} className="input-dt" /></Fld>
          <Fld l="Email"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input-dt" /></Fld>
          <Fld l="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="input-dt" /></Fld>
          <Fld l="Credit limit (€)"><Input type="number" value={f.credit_limit_eur} onChange={(e) => setF({ ...f, credit_limit_eur: Number(e.target.value) })} className="input-dt" /></Fld>
          <Fld l="Commission %"><Input type="number" step="0.1" value={f.commission_pct} onChange={(e) => setF({ ...f, commission_pct: Number(e.target.value) })} className="input-dt" data-testid="p-commission" /></Fld>
          <Fld l="Direction">
            <select value={f.commission_direction} onChange={(e) => setF({ ...f, commission_direction: e.target.value })} className="input-dt">
              <option value="partner_earns">Partner earns commission</option>
              <option value="partner_pays_less">Partner gets discount</option>
            </select>
          </Fld>
          <Fld l="Billing cycle">
            <select value={f.billing_cycle} onChange={(e) => setF({ ...f, billing_cycle: e.target.value })} className="input-dt" data-testid="p-billing">
              <option value="per_booking">Per booking (pre-paid)</option>
              <option value="monthly">Monthly invoice</option>
            </select>
          </Fld>
          <Fld l="Código postal (zona)"><Input value={f.postal_code} onChange={(e) => setF({ ...f, postal_code: e.target.value })} placeholder="1200-234" className="input-dt" data-testid="p-postal" /></Fld>
          <Fld l="Cliente pode pagar no widget?">
            <select value={f.allow_client_payment ? "yes" : "no"} onChange={(e) => setF({ ...f, allow_client_payment: e.target.value === "yes" })} className="input-dt" data-testid="p-allow-client-pay">
              <option value="no">Não (fatura ao parceiro)</option>
              <option value="yes">Sim (MB Way / MB / Cartão)</option>
            </select>
          </Fld>
          <Fld l="Fatura genérica (sem nome)?">
            <select value={f.invoice_allow_generic ? "yes" : "no"} onChange={(e) => setF({ ...f, invoice_allow_generic: e.target.value === "yes" })} className="input-dt" data-testid="p-invoice-generic">
              <option value="yes">Sim (permite consumidor final)</option>
              <option value="no">Não (exige Nome / NIF)</option>
            </select>
          </Fld>
        </div>
        <div className="flex gap-3 mt-6">
          <GhostButton type="button" onClick={onClose} className="flex-1">{t("action_cancel")}</GhostButton>
          <GoldButton type="submit" disabled={loading} className="flex-1 justify-center" data-testid="p-submit">{t("partners_create")}</GoldButton>
        </div>
      </form>
    </div>
  );
}
function Fld({ l, children }) { return (<div><label className="label-caps block mb-2">{l}</label>{children}</div>); }
